import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Trash2, Plus, Minus, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { getCart, updateCartItem, removeFromCart, clearCart } from '@/lib/api';
import { formatPrice, getImageUrl } from '@/lib/utils';
import { toast } from 'sonner';
import api from '@/lib/api';
import { PageSEO } from '@/components/SEO';

export default function Cart() {
  const [cart, setCart] = useState([]);
  const [loading, setLoading] = useState(true);
  const [storeSettings, setStoreSettings] = useState({
    taxRate: 0,
    freeShippingThreshold: 100,
    defaultShippingRate: 0
  });

  useEffect(() => {
    loadCart();
    loadStoreSettings();
  }, []);

  const loadCart = async () => {
    try {
      const res = await getCart();
      setCart(res.data);
    } catch (error) {
      toast.error('Failed to load cart');
    } finally {
      setLoading(false);
    }
  };

  const loadStoreSettings = async () => {
    try {
      const res = await api.get('/site-builder/store-settings');
      const settings = res.data;
      // tax_rate is stored as decimal (0.09 = 9%), null means 0
      const taxRateDecimal = settings.tax_rate === null || settings.tax_rate === undefined ? 0 : settings.tax_rate;
      setStoreSettings({
        taxRate: taxRateDecimal * 100, // Convert from decimal to percentage for display
        freeShippingThreshold: settings.free_shipping_threshold ?? 100,
        defaultShippingRate: settings.default_shipping_rate ?? 0
      });
    } catch (error) {
      console.error('Failed to load store settings:', error);
      // Keep default values if settings fail to load
    }
  };

  const handleUpdateQuantity = async (cartItemId, quantity) => {
    if (quantity < 1) return;
    try {
      await updateCartItem(cartItemId, { quantity });
      loadCart();
    } catch (error) {
      toast.error('Failed to update quantity');
    }
  };

  const handleRemove = async (cartItemId) => {
    try {
      await removeFromCart(cartItemId);
      toast.success('Item removed');
      loadCart();
    } catch (error) {
      toast.error('Failed to remove item');
    }
  };

  const handleClearCart = async () => {
    if (!confirm('Are you sure you want to clear your cart?')) return;
    try {
      await clearCart();
      toast.success('Cart cleared');
      loadCart();
    } catch (error) {
      toast.error('Failed to clear cart');
    }
  };

  const subtotal = cart.reduce((sum, item) => {
    return sum + (item.product?.price || 0) * item.quantity;
  }, 0);

  const taxRate = storeSettings.taxRate / 100; // Convert percentage to decimal
  const tax = subtotal * taxRate;
  const shipping = subtotal >= storeSettings.freeShippingThreshold ? 0 : storeSettings.defaultShippingRate;
  const total = subtotal + tax + shipping;

  if (loading) {
    return (
      <div className="min-h-screen pt-32 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="h-8 skeleton rounded w-48 mb-8" />
          <div className="space-y-4">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="h-32 skeleton rounded" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (cart.length === 0) {
    return (
      <div className="min-h-screen pt-32 pb-16" data-testid="empty-cart">
        <PageSEO.cart />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-medium mb-4">Your cart is empty</h1>
          <p className="text-lg text-stone-600 mb-8">Start shopping to add items to your cart</p>
          <Link to="/shop">
            <Button className="btn-primary">Shop Now</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-32 pb-16" data-testid="cart-page">
      <PageSEO.cart />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-4xl md:text-5xl font-medium">Shopping Cart</h1>
          <Button variant="ghost" onClick={handleClearCart} className="text-stone-600">
            Clear Cart
          </Button>
        </div>

        <div className="grid lg:grid-cols-3 gap-12">
          {/* Cart Items */}
          <div className="lg:col-span-2 space-y-6">
            {cart.map((item) => (
              <div
                key={item.cart_item_id}
                className="flex gap-6 bg-white border border-stone-200 rounded-lg p-6"
                data-testid={`cart-item-${item.product?.product_id}`}
              >
                <Link to={`/product/${item.product?.product_id}`} className="flex-shrink-0">
                  <img
                    src={getImageUrl(item.product?.images?.[0])}
                    alt={item.product?.name}
                    className="w-24 h-32 object-cover rounded"
                  />
                </Link>

                <div className="flex-1 flex flex-col">
                  <div className="flex justify-between mb-2">
                    <div>
                      <p className="text-xs uppercase tracking-widest text-stone-500">
                        {item.product?.collection}
                      </p>
                      <h3 className="font-light text-lg">{item.product?.name}</h3>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleRemove(item.cart_item_id)}
                      data-testid="remove-button"
                    >
                      <Trash2 className="h-5 w-5" />
                    </Button>
                  </div>

                  <p className="text-sm text-stone-600 mb-4">{item.product?.material}</p>

                  <div className="flex justify-between items-center mt-auto">
                    <div className="flex items-center gap-3">
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={() => handleUpdateQuantity(item.cart_item_id, item.quantity - 1)}
                        disabled={item.quantity <= 1}
                      >
                        <Minus className="h-4 w-4" />
                      </Button>
                      <span className="w-12 text-center" data-testid="item-quantity">{item.quantity}</span>
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={() => handleUpdateQuantity(item.cart_item_id, item.quantity + 1)}
                        disabled={item.quantity >= (item.product?.inventory || 0)}
                      >
                        <Plus className="h-4 w-4" />
                      </Button>
                    </div>
                    <span className="text-lg font-light" data-testid="item-total">
                      {formatPrice((item.product?.price || 0) * item.quantity)}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <div className="bg-stone-50 border border-stone-200 rounded-lg p-8 sticky top-32">
              <h2 className="text-2xl font-medium mb-6">Order Summary</h2>

              <dl className="space-y-4 mb-6">
                <div className="flex justify-between text-base">
                  <dt className="text-stone-600">Subtotal</dt>
                  <dd className="font-light" data-testid="subtotal">{formatPrice(subtotal)}</dd>
                </div>
                {storeSettings.taxRate > 0 && (
                  <div className="flex justify-between text-base">
                    <dt className="text-stone-600">GST ({storeSettings.taxRate}%)</dt>
                    <dd className="font-light" data-testid="tax">{formatPrice(tax)}</dd>
                  </div>
                )}
                <div className="flex justify-between text-base">
                  <dt className="text-stone-600">Shipping</dt>
                  <dd className="font-light" data-testid="shipping">
                    {shipping === 0 ? 'FREE' : formatPrice(shipping)}
                  </dd>
                </div>
                {storeSettings.defaultShippingRate > 0 && subtotal < storeSettings.freeShippingThreshold && (
                  <p className="text-sm text-stone-500">
                    Spend {formatPrice(storeSettings.freeShippingThreshold - subtotal)} more for free shipping
                  </p>
                )}
                <div className="border-t border-stone-300 pt-4">
                  <div className="flex justify-between text-lg">
                    <dt className="font-medium">Total</dt>
                    <dd className="font-medium" data-testid="total">{formatPrice(total)}</dd>
                  </div>
                </div>
              </dl>

              <Link to="/checkout">
                <Button className="w-full btn-primary" size="lg" data-testid="checkout-button">
                  Proceed to Checkout
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>

              <Link to="/shop" className="block text-center mt-4 text-sm text-stone-600 hover:text-stone-900">
                Continue Shopping
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
